import {
  EMPLOYMENT_LABELS,
  SENIORITY_LABELS,
  fmtRecent,
  label,
  regionsLabel,
  salaryLabel,
  plural,
} from '@remote-medium/shared';
import { Button, errorMessage, unwrap, useApi } from '@remote-medium/ui';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { admin } from '../../lib/client.ts';
import {
  ConfirmButton,
  DataTable,
  None,
  Notice,
  Page,
  PageHeader,
  SectionHeader,
  StatusRow,
} from '../../lib/ui.tsx';

function RoleHead() {
  return (
    <thead>
      <tr>
        <th>Role</th>
        <th>Seniority</th>
        <th>Type</th>
        <th>Salary</th>
        <th>Regions</th>
        <th>Posted</th>
      </tr>
    </thead>
  );
}

export function DigestPreviewPage() {
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi(
    () => unwrap(admin.jobs.digest.preview.$get()),
    [],
  );
  const [msg, setMsg] = useState<string | null>(null);

  const totalRoles = data?.reduce((n, e) => n + e.roles.length, 0) ?? 0;

  async function send() {
    setMsg(null);
    try {
      const r = await unwrap(admin.jobs.digest.$post());
      void navigate('/ops', {
        state: {
          digestMsg:
            `Digest sent: ${r.candidates} candidates, ${r.sent} emails sent` +
            (r.failed ? `, ${r.failed} failed` : '') +
            '.',
        },
      });
    } catch (e) {
      setMsg(errorMessage(e));
      throw e;
    }
  }

  const sendButton = (
    <ConfirmButton
      variant="default"
      disabled={!data?.length}
      title="Send the digest now?"
      description={`${plural(data?.length ?? 0, 'candidate')} get an email listing ${plural(totalRoles, 'role')} in total. Each role is only ever sent to a candidate once.`}
      confirmLabel="Send digest"
      onConfirm={send}
    >
      Send digest now
    </ConfirmButton>
  );

  return (
    <Page>
      <PageHeader
        back={{ to: '/ops', label: 'Ops' }}
        title="Digest preview"
        description="Exactly what a send would email right now: only candidates with at least one eligible role they haven't been sent. Nothing is recorded or emailed until you send."
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => reload()}>
              <RefreshCw data-icon="inline-start" />
              Refresh
            </Button>
            {sendButton}
          </>
        }
      />

      {msg && <Notice tone="error">{msg}</Notice>}
      {(loading || error) && (
        <DataTable>
          <RoleHead />
          <tbody>
            <StatusRow colSpan={6} loading={loading} error={error} />
          </tbody>
        </DataTable>
      )}

      {!loading && !error && data && data.length === 0 && (
        <Notice>No candidate has an eligible, unsent role right now. Nothing to send.</Notice>
      )}

      {!loading && !error && data && data.length > 0 && (
        <>
          <p className="m-0 text-sm text-muted-foreground">
            {plural(data.length, 'candidate')} · {plural(totalRoles, 'role')} in total
          </p>
          {data.map((entry) => (
            <section key={entry.candidateId} className="flex flex-col gap-3">
              <SectionHeader
                title={entry.fullName}
                aside={`${entry.email} · ${plural(entry.roles.length, 'role')}`}
              />
              <DataTable>
                <RoleHead />
                <tbody>
                  {entry.roles.map((r) => (
                    <tr key={r.roleId}>
                      <td className="max-w-72">
                        <div className="font-semibold">{r.title}</div>
                        <div className="text-muted-foreground">
                          {[r.sourceCompanyName, r.roleTypeName].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="whitespace-nowrap">
                        {r.seniority ? label(SENIORITY_LABELS, r.seniority) : <None>Any</None>}
                      </td>
                      <td className="whitespace-nowrap">
                        {r.employmentType ? (
                          label(EMPLOYMENT_LABELS, r.employmentType)
                        ) : (
                          <None>Any</None>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {salaryLabel(r) ?? <None>Not given</None>}
                      </td>
                      <td className="max-w-56 text-muted-foreground">{regionsLabel(r.regions)}</td>
                      <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                        {fmtRecent(r.postedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </section>
          ))}
          <div className="flex justify-end">{sendButton}</div>
        </>
      )}
    </Page>
  );
}
